import { useEffect, useState, useContext } from 'react';
import { useNavigate } from 'react-router';
import Box from '@mui/material/Box';
import Link from '@mui/material/Link';
import { addWeeks } from 'date-fns';

import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { DesktopDatePicker } from '@mui/x-date-pickers/DesktopDatePicker';
import {
  DATE_FORMAT_STRING,
  getDateOnly,
} from '#common/utils';
import {
  useGetInventorySalesReportMutation,
} from '#services/inventory';
import { FeedbackUIContext } from '#context/feedback-ui-context';
import { EnhancedTableProps } from '#components/EnhancedTable';

import InventoryReportTable from './ReportTable';

const PAGE_SIZE = 30;

type ReportTrigger = (arg: Record<string, unknown>) => Promise<{ data?: unknown; error?: unknown }>;

interface SalesCollection {
  id: string;
  name: string;
  inactivated?: boolean;
}

interface SalesReportRow {
  productName?: string;
  color?: string;
  productId?: string | number;
  sizes: Record<string, number>;
  [key: string]: unknown;
}

interface SalesReport {
  collectionId: string;
  allSizes: Array<string | number>;
  rows: Array<Record<string, unknown>>;
  collection: SalesCollection;
  [key: string]: unknown;
}

export default function InventorySalesReport() {
  const navigate = useNavigate();
  const [getReportRaw] = useGetInventorySalesReportMutation();
  const getReport = getReportRaw as unknown as ReportTrigger;
  const [reports, setReports] = useState<SalesReport[]>([]);
  const [dateFrom, setDateFrom] = useState<Date | null>(addWeeks(getDateOnly(), -1));
  const [dateTo, setDateTo] = useState<Date | null>(getDateOnly());

  const {
    simpleSnackbar,
  } = useContext(FeedbackUIContext)!;

  const queryOptions: { pageSize: number; dateFrom?: number; dateTo?: number } = {
    pageSize: PAGE_SIZE,
  };

  if (dateFrom) {
    queryOptions.dateFrom = dateFrom.getTime();
  }
  if (dateTo) {
    queryOptions.dateTo = dateTo.getTime();
  }

  useEffect(() => {
    void getReport({
      ...queryOptions,
    }).then((res) => {
      const hasError = simpleSnackbar(res as unknown as Parameters<typeof simpleSnackbar>[0]);
      if (hasError) {
        return;
      }

      const {
        reports: reportList,
        categories,
      } = (res as { data: { reports: SalesReport[]; categories: { collections: SalesCollection[] } } }).data;

      const mappedReports = reportList.map((r) => {
        const find = categories.collections.find((c) => c.id === r.collectionId);
        if (!find) {
          console.log('not exits r.collectionId:', r.collectionId);
          return null;
        }
        return {
          ...r,
          collection: find,
        };
      }).filter((c): c is SalesReport => Boolean(c));

      setReports(mappedReports);
    });
  }, [dateFrom, dateTo]);

  const sortedCollection = reports
    .sort((a, b) => {
      if (a.collection.name > b.collection.name) {
        return 1;
      }
      if (a.collection.name < b.collection.name) {
        return -1;
      }
      return 0;
    }).map((r) => r.collection);

  return (
    <>
      <>
        <Box sx={{ typography: 'h7' }}>
          Inventory Sales Report
        </Box>

        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            bgcolor: 'background.paper',
            borderRadius: 1,
          }}
        >
          <Box />

          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <LocalizationProvider
              dateAdapter={AdapterDateFns}
            >
              From:&nbsp;
              <DesktopDatePicker
                format={DATE_FORMAT_STRING}
                value={dateFrom}
                {...(dateTo ? { maxDate: dateTo } : {})}
                slotProps={{ textField: { size: 'small' } }}
                onChange={(d) => {
                  setDateFrom(d);
                  if ((dateTo as Date | null | undefined) === undefined) {
                    setDateTo(new Date());
                  }
                }}
              />
              &nbsp;To:&nbsp;
              <DesktopDatePicker
                format={DATE_FORMAT_STRING}
                value={dateTo}
                {...(dateFrom ? { minDate: dateFrom } : {})}
                maxDate={new Date()}
                slotProps={{ textField: { size: 'small' } }}
                onChange={(d) => {
                  setDateTo(d);
                  if ((dateFrom as Date | null | undefined) === undefined) {
                    setDateFrom(new Date());
                  }
                }}
              />

            </LocalizationProvider>
          </Box>
        </Box>
      </>

      {sortedCollection.length === 0 && (
        'No matching sales found'
      )}

      {
        sortedCollection.length !== 0 && sortedCollection.map((collection) => {
          const report = reports.find((r) => r.collectionId === collection.id);
          if (!report) {
            return null;
          }

          const tableHeaders: EnhancedTableProps['headers'] = [{
            id: 'productName',
            label: 'Style',
            renderer: (row) => {
              const r = row as unknown as SalesReportRow;
              const url = `/shop/add?productId=${r.productId}&inventory=true`;
              return (
                <Link
                  href={url}
                  onClick={(e) => {
                    e.preventDefault();
                    void navigate(url);
                  }}
                  sx={{ textDecoration: 'inherit' }}
                >
                  {r.productName}
                </Link>
              );
            },
          }, {
            id: 'color',
            label: 'Color',
            renderer: (row) => (row as unknown as SalesReportRow).color,
          }];

          // build size headers
          report.allSizes.forEach((s) => {
            tableHeaders.push({
              id: `size|${s}`,
              label: `Size ${s}`,
              renderer: (row) => {
                const raw = (row as unknown as SalesReportRow).sizes[s];
                let amounts: string | number;
                if (raw === undefined) {
                  amounts = '-';
                } else if (raw === 0) {
                  amounts = '';
                } else {
                  amounts = raw * -1;
                }

                return amounts;
              },
            });
          });

          const tableProps = {
            name: 'sales-report-table',

            headers: tableHeaders,

            pinnedColumns: {
              left: ['productName', 'color'],
            },

            pageSize: PAGE_SIZE,
          };

          return (
            <InventoryReportTable
              key={collection.id}
              collection={collection}
              report={report as unknown as Parameters<typeof InventoryReportTable>[0]['report']}
              tableProps={tableProps}
              getReport={getReport}
              queryOptions={queryOptions}
            />
          );
        })
      }
    </>
  );
}
